import React, { FC, useEffect, useState } from 'react';

import { Drawer } from 'antd';

import {
  faBars,
  faSignOutAlt,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DARKBG_PATHS } from '@mock/ThemeSettings';
import { UserType } from '@pages/dashboard/SignUp/SignUp';
import { setToken } from '@store/auth';
import { Auth } from 'aws-amplify';
import { clsx } from 'clsx';
import { useDispatch } from 'react-redux';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

interface MenuItem {
  key: string;
  label: string;
}

interface ProfileHeaderProps {
  type: UserType;
}

const menues = {
  [UserType.PRODUCER]: [
    { key: '/', label: 'Centinel' },
    { key: '/producer-profile', label: 'My profile' },
    { key: '/place-new-policy', label: 'Place new plan' },
    { key: '/all-policies', label: 'View all plans' },
    { key: '/all-affiliates', label: 'View all Affiliates' },
  ],
  [UserType.AFFILIATE]: [
    { key: '/', label: 'Centinel' },
    { key: '/affiliate-profile', label: 'My Profile' },
    { key: '/all-producers', label: 'View all Producers' },
    { key: '/all-plans', label: 'View all Plans' },
  ],
};

const ProfileHeader: FC<ProfileHeaderProps> = ({ type }) => {
  const [isLogged, setIsLogged] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isBgDark = DARKBG_PATHS.includes(pathname);

  useEffect(() => {
    const checkIsAuth = async () => {
      try {
        const user = await Auth.currentSession();
        const accessToken = user.getIdToken();
        const token = accessToken.getJwtToken();
        dispatch(setToken({ token }));
        setIsLogged(true);
      } catch (error) {}
    };
    checkIsAuth().then();
  }, [dispatch]);

  const items: MenuItem[] =
    menues[type as UserType.AFFILIATE | UserType.PRODUCER] || [];

  const signOut = async () => {
    try {
      await Auth.signOut();
      navigate('/');
    } catch (error) {}
  };

  return (
    <header className="w-full max-h-[50px] h-full border-solid border-x-0 border-t-0 border-b-2 border-b-cyanDark flex items-center justify-center z-20 print:hidden">
      <div className="max-w-[1140px] py-1 px-[15px] w-full h-[54px] flex justify-between items-center">
        {/* Always visible Centinel */}
        <NavLink
          to="/"
          className={clsx(
            'capitalize text-base leading-[100%] flex items-center justify-center font-extrabold',
            { 'text-blue': !isBgDark, 'text-white': isBgDark },
          )}
        >
          Centinel
        </NavLink>

        {/* Desktop Menu */}
        <nav className="hidden md:flex justify-between text-center ml-4 flex-1">
          <ul className="list-none flex uppercase h-full p-0 m-0 justify-center items-center">
            {items
              .filter((item) => item.label !== 'Centinel')
              .map((menuItem, index) => {
                return (
                  <NavLink
                    to={menuItem.key}
                    key={index}
                    className={({ isActive }) =>
                      clsx(
                        'hover:bg-menuBlue h-full text-center p-[15px] transition-all duration-200 cursor-pointer uppercase',
                        {
                          'text-blue': !isBgDark,
                          'text-white': isBgDark,
                          'bg-oceanBlue text-blue font-extrabold shadow-md':
                            isActive && !isBgDark,
                          'bg-menuBlue text-white font-extrabold shadow-md':
                            isActive && isBgDark,
                        },
                      )
                    }
                  >
                    <li>{menuItem.label}</li>
                  </NavLink>
                );
              })}
          </ul>
        </nav>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden text-white bg-cyanDark outline-none border-none py-1 cursor-pointer"
          onClick={() => setDrawerOpen(true)}
        >
          <FontAwesomeIcon icon={faBars} size="lg" />
        </button>

        {/* Logout Icon (desktop) */}
        {isLogged && (
          <div className="hidden md:block ml-4">
            <FontAwesomeIcon
              icon={faSignOutAlt}
              className={clsx(
                `self-center text-2xl cursor-pointer hover:text-cyan transition-colors`,
                { 'text-blue': !isBgDark, 'text-white': isBgDark },
              )}
              onClick={signOut}
            />
          </div>
        )}
      </div>

      {/* Drawer for mobile */}
      <Drawer
        width={300}
        placement="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        bodyStyle={{ backgroundColor: '#1E3A8A', padding: 0 }}
        headerStyle={{ backgroundColor: '#1E3A8A', borderBottom: 'none' }}
        closeIcon={
          <FontAwesomeIcon icon={faTimes} className="text-white text-2xl" />
        }
      >
        <ul className="list-none p-0 m-0 flex flex-col uppercase">
          {items
            .filter((item) => item.label !== 'Centinel') // <-- exclude Centinel
            .map((menuItem, index) => (
              <NavLink
                to={menuItem.key}
                key={index}
                className={({ isActive }) =>
                  clsx(
                    'px-4 py-3 transition-all hover:bg-menuBlue duration-200 cursor-pointer text-white',
                    {
                      'bg-menuBlue font-extrabold shadow-md': isActive,
                    },
                  )
                }
                onClick={() => setDrawerOpen(false)}
              >
                {menuItem.label}
              </NavLink>
            ))}
          {isLogged && (
            <li
              className="p-4 text-white cursor-pointer hover:bg-menuBlue transition-all duration-200 flex items-center gap-2"
              onClick={() => {
                setDrawerOpen(false);
                signOut();
              }}
            >
              <FontAwesomeIcon icon={faSignOutAlt} /> Logout
            </li>
          )}
        </ul>
      </Drawer>
    </header>
  );
};

export default ProfileHeader;
